(() => {
  if (customElements.get('hh-designer-mode')) return;
  class DesignerMode extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.designer = this.closest('hh-frame-designer');
      if (!this.designer) return;
      this.media = matchMedia('(max-width: 900px)');
      let saved;
      try { saved = sessionStorage.getItem('hh-designer-mode'); } catch {}
      this.explicit = ['mobile', 'desktop'].includes(saved);
      this.apply(this.explicit ? saved : this.media.matches ? 'mobile' : 'desktop');
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-designer-mode]');
        if (button) {
          this.explicit = true;
          this.apply(button.dataset.designerMode);
          try { sessionStorage.setItem('hh-designer-mode', button.dataset.designerMode); } catch {}
        }
        if (event.target.closest('[data-mode-preview]')) {
          const expanded = this.designer.dataset.mobilePreview !== 'large';
          this.designer.dataset.mobilePreview = expanded ? 'large' : 'compact';
          this.querySelector('[data-mode-preview]').setAttribute('aria-expanded', String(expanded));
          this.querySelector('[data-mode-preview]').textContent = expanded ? this.dataset.previewSmall : this.dataset.previewLarge;
          this.redraw();
        }
      }, { signal });
      this.media.addEventListener('change', () => {
        if (!this.explicit) this.apply(this.media.matches ? 'mobile' : 'desktop');
      }, { signal });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.designer, { childList: true, subtree: true, characterData: true });
      this.designer.addEventListener('focusin', () => this.syncKeyboard(), { signal });
      this.designer.addEventListener('focusout', () => queueMicrotask(() => this.syncKeyboard()), { signal });
      window.visualViewport?.addEventListener('resize', () => this.syncKeyboard(), { signal });
      this.sync();
    }
    apply(mode) {
      if (!['mobile', 'desktop'].includes(mode)) return;
      this.designer.dataset.designerMode = mode;
      this.querySelectorAll('[data-designer-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.designerMode === mode)));
      const note = this.querySelector('[data-mode-note]');
      if (note) note.textContent = mode === 'mobile' ? this.dataset.mobileNote : this.dataset.desktopNote;
      this.querySelector('[data-mode-preview]').hidden = mode !== 'mobile';
      this.redraw();
    }
    redraw() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected || !this.designer.ready) return;
        this.designer.renderTextResizeHandles?.();
        this.designer.renderArtworkSelection?.();
      });
    }
    syncKeyboard() {
      if (!this.isConnected) return;
      const active = document.activeElement;
      const typing = this.designer.contains(active) && active?.matches('textarea,input:not([type=checkbox]):not([type=radio]):not([type=range]),[contenteditable=true]');
      this.designer.toggleAttribute('data-mode-typing', Boolean(typing));
    }
    sync() {
      const source = this.designer.querySelector('[data-price]');
      const output = this.querySelector('[data-mode-price]');
      if (source && output && output.textContent !== source.textContent) output.textContent = source.textContent;
      const colors = this.designer.querySelector('[data-heading-colors]');
      const inspector = this.designer.querySelector('[data-studio-inspector]');
      if (!colors || !inspector || !this.designer.studioReady) return;
      if (!this.colorHome) {
        this.colorHome = document.createComment('');
        colors.before(this.colorHome);
      }
      const headingSelected = this.designer.activePanel === 'words' && this.designer.studioSelection?.()?.kind === 'heading';
      if (headingSelected) {
        const fontField = inspector.querySelector('[data-studio-font-field]');
        if (fontField && colors.parentElement !== fontField.parentElement) fontField.before(colors);
      } else if (this.colorHome.parentElement && colors.previousSibling !== this.colorHome) {
        this.colorHome.after(colors);
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      const colors = this.designer?.querySelector('[data-heading-colors]');
      if (colors && this.colorHome?.parentElement) this.colorHome.after(colors);
      this.colorHome?.remove();
      this.colorHome = null;
      cancelAnimationFrame(this.frame);
    }
  }
  customElements.define('hh-designer-mode', DesignerMode);
})();
